import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CopyIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { AxiosError } from 'axios';

import { RelevoInterface } from '../../../../api/types';
import { ItemsSummaryAccordion } from '../../../../common/components/ItemsSummaryModal/ItemsSummaryAccordion';
import { FailureItem } from '../../../../common/components/ItemsSummaryModal/ItemsSummaryModal';
import { ProcessSpinner } from '../../../../common/components/ProcessSpinner/ProcessSpinner';
import { TableType } from '../../../../common/components/Table/model/enums/table-type.enum';
import { Table } from '../../../../common/components/Table/Table';
import { HttpException } from '../../../../common/models/httpException';
import { useHtmlTranslations, useTranslations } from '../../../../contexts/LocalizationContext';
import { useLocalizationContext } from '../../../../contexts/LocalizationContext/useLocalizationContext.hook';
import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';
import { useAssignItems } from '../../../../services/relevos';
import { AssignItemsConfirmFooter } from '../AssignItemsConfirmFooter/AssignItemsConfirmFooter';
import { AssignItemsResultStatus, AssignItemsSuccessBody } from '../AssignItemsSuccessBody/AssignItemsSuccessBody';
import { buildKnownDeliveryNoteContext } from '../DeliveryNote/deliveryNoteUtils';
import { downloadDeliveryNotePdf } from '../DeliveryNote/downloadDeliveryNotePdf';
import { downloadNotAssignedItemsCsv } from './utils/exportErrorListCsv';

type AssignItemsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AssignItemsModal = ({ isOpen, onClose }: AssignItemsModalProps) => {
  const { language } = useLocalizationContext();
  const translations = useTranslations();
  const htmlTranslations = useHtmlTranslations();
  const {
    restaurantId,
    restaurant,
    scannedRelevos,
    applicationUser,
    quickContact,
    setScannedRelevos,
    setAssignedRelevos,
    setApplicationUser,
    setQuickContact,
    isDeliveryNoteEnabled,
  } = useScannedRelevosContext();
  const {
    mutateAsync: assignItems,
    isLoading: isAssigningItems,
    data: itemsAssignedData,
    reset: resetAssignItems,
  } = useAssignItems();
  const [shouldResetUserAfterClose, setShouldResetUserAfterClose] = useState(false);
  const [shouldClearScannedAfterClose, setShouldClearScannedAfterClose] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      resetAssignItems();
      setShouldResetUserAfterClose(false);
      setShouldClearScannedAfterClose(false);
    }
  }, [isOpen, resetAssignItems]);

  const executeAssign = async () => {
    if (restaurantId && (applicationUser || quickContact)) {
      const userId = applicationUser?.id || quickContact?.id || '';
      const relevosUids = scannedRelevos.map((relevo) => relevo.uId);

      try {
        const response = await assignItems({
          userId,
          restaurantId,
          relevos: relevosUids,
          quickContactType: quickContact?.type,
        });

        setAssignedRelevos(response);
        setShouldResetUserAfterClose(true);
        setShouldClearScannedAfterClose(true);

        if (quickContact && isDeliveryNoteEnabled) {
          const deliveryNoteContext = buildKnownDeliveryNoteContext({
            assignedItems: response.assignedItems,
            scannedRelevos,
            quickContactName: quickContact.quickContactName,
            quickContactAddress: quickContact.address,
            restaurantName: restaurant?.restaurantName || null,
            restaurantAddress: restaurant?.address || null,
            orderNumber: response.orderNumber,
            language,
            unknownItemLabel: translations('assign_unknown_items_product_placeholder') || 'Unknown item',
          });

          if (deliveryNoteContext) {
            try {
              await downloadDeliveryNotePdf({
                context: deliveryNoteContext,
                translations,
              });
            } catch (pdfError) {
              console.error('[AssignItemsModal] Failed to download delivery note PDF', pdfError);
            }
          }
        }
      } catch (error) {
        setShouldResetUserAfterClose(false);
        setShouldClearScannedAfterClose(false);
        const axiosError = error as AxiosError<HttpException>;
        toast.error(axiosError.response?.data.message || translations('something_went_wrong'));
      }
    }
  };

  const handleCopyUid = useCallback(
    async (uid: string) => {
      try {
        await navigator.clipboard.writeText(uid);
        toast.success(translations('copied_to_clipboard'));
      } catch (e) {
        toast.error(translations('something_went_wrong'));
      }
    },
    [translations],
  );

  const handleDownloadErrorList = useCallback(() => {
    const items = itemsAssignedData?.notAssignedItems || [];
    if (!items.length) return;
    downloadNotAssignedItemsCsv({ items, language, translations });
  }, [itemsAssignedData, language, translations]);

  const handleClose = () => {
    if (shouldResetUserAfterClose) {
      setApplicationUser(null);
      setQuickContact(null);
      setShouldResetUserAfterClose(false);
    }
    if (shouldClearScannedAfterClose) {
      setScannedRelevos([]);
      setShouldClearScannedAfterClose(false);
    }
    onClose();
  };

  const columns = useMemo<ColumnDef<RelevoInterface>[]>(
    () => [
      {
        id: 'category',
        header: translations('item_category'),
        cell: ({ row }) => row.original.category.translations[language],
      },
      { accessorKey: 'productName', header: translations('item_name') },
    ],
    [language, translations],
  );

  const failureColumns = useMemo<ColumnDef<FailureItem>[]>(
    () => [
      {
        header: translations('item_category'),
        cell: ({ row }) => row.original.product?.category.translations[language],
      },
      {
        id: 'name',
        header: translations('item_name'),
        cell: ({ row }) => row.original.product?.productName,
      },
      {
        id: 'uid',
        header: translations('relevo_uid'),
        cell: ({ row }) => {
          const uid = row.original.product?.uId || '';
          if (!uid) return null;
          return (
            <HStack spacing={1} align="center" justify="center" whiteSpace="nowrap">
              <Text noOfLines={1} title={uid} whiteSpace="nowrap">
                {uid}
              </Text>
              <Tooltip label={translations('copy')}>
                <IconButton
                  aria-label={translations('copy')}
                  variant="ghost"
                  minW="auto"
                  icon={<CopyIcon />}
                  onClick={() => handleCopyUid(uid)}
                />
              </Tooltip>
            </HStack>
          );
        },
      },
      { accessorKey: 'message', header: translations('error') },
    ],
    [language, translations, handleCopyUid],
  );

  const assignedItemsCount = itemsAssignedData?.assignedItems.length ?? scannedRelevos.length ?? 0;
  const notAssignedItemsCount = itemsAssignedData?.notAssignedItems.length ?? 0;
  const totalAttemptedItems = itemsAssignedData ? assignedItemsCount + notAssignedItemsCount : 0;

  let assignResultStatus: AssignItemsResultStatus | undefined = undefined;
  if (itemsAssignedData) {
    if (notAssignedItemsCount && assignedItemsCount) {
      assignResultStatus = 'partial';
    } else if (notAssignedItemsCount && !assignedItemsCount) {
      assignResultStatus = 'failure';
    } else {
      assignResultStatus = 'success';
    }
  }

  const assignmentUserLabel =
    quickContact?.quickContactName || applicationUser?.userId || quickContact?.id || applicationUser?.id || '';

  return (
    <Modal onClose={handleClose} isOpen={isOpen} isCentered size={'4xl'} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent p={2} display="flex" flexDirection="column">
        {!isAssigningItems && !itemsAssignedData ? (
          <>
            <ModalHeader>
              <Text pb={4} fontSize={20}>
                {htmlTranslations('assign_items_confirm', {
                  '{{user-id}}': applicationUser?.userId || quickContact?.quickContactName || '',
                })}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={8} flex="1">
              <Table
                type={TableType.SIMPLE}
                variant="relevoSummary"
                data={scannedRelevos}
                columns={columns}
                maxH={'40vh'}
                overflowY="auto"
              />
            </ModalBody>
            <ModalFooter>
              <AssignItemsConfirmFooter onConfirm={executeAssign} onCancel={handleClose} isLoading={isAssigningItems} />
            </ModalFooter>
          </>
        ) : null}
        {isAssigningItems ? (
          <ModalBody p={2} flex="1" overflowY="auto">
            <ProcessSpinner />
          </ModalBody>
        ) : null}
        {itemsAssignedData ? (
          <>
            <ModalCloseButton />
            <ModalBody p={8} flex="1">
              <AssignItemsSuccessBody
                status={assignResultStatus}
                assignedCount={assignedItemsCount}
                totalCount={totalAttemptedItems}
                userLabel={assignmentUserLabel}
                orderNumber={itemsAssignedData.orderNumber ?? null}
                showProgress={assignResultStatus === 'success'}
                onAnimationComplete={handleClose}
              />
              {!!itemsAssignedData && itemsAssignedData.notAssignedItems?.length ? (
                <ItemsSummaryAccordion
                  buttonText={translations('scanner_assigned_items_summary_modal_failure_content')}
                  isOpen={false}
                >
                  <Table
                    type={TableType.SIMPLE}
                    variant="relevoSummary"
                    data={itemsAssignedData.notAssignedItems}
                    columns={failureColumns}
                    maxH={'38vh'}
                    overflowY="auto"
                  />
                  <Flex w="100%" justify="center" mt={4}>
                    <Button variant="orangeSolid" onClick={handleDownloadErrorList}>
                      {translations('download_error_list_csv')}
                    </Button>
                  </Flex>
                </ItemsSummaryAccordion>
              ) : null}
            </ModalBody>
          </>
        ) : null}
      </ModalContent>
    </Modal>
  );
};
